import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/lib/hooks';
import {
    IconArrowLeft, IconUpload, IconDownload, IconSparkles,
    IconX, IconAlertCircle, IconEye,
    IconPhoto, IconPlus, IconThumbUp, IconThumbDown, IconLoader2, IconEdit,
    IconGenderMale, IconGenderFemale, IconMoodKid, IconShoppingBag, IconUsers,
    IconCalendar, IconHeart, IconHeartFilled, IconForms,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { notifications } from '@/lib/notifications';
import aiTemplateService from '../../../services/aiTemplateService';
import { resolveImageInputLabel } from '../../../data/imageInputLabels';
import aiTemplateCategoryService from '../../../services/aiTemplateCategoryService';
import imageGenerationService from '../../../services/imageGenerationService';
import paymentService from '../../../services/paymentService';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import PhotoBoothInbox from '../../../components/common/PhotoBoothInbox';
import PrintModal from '../generate-image/PrintModal';
import CustomDownloadModal from '../generate-image/CustomDownloadModal';
import SelectField from '../../../components/common/SelectField';
import DatePickerField from '../../../components/common/DatePickerField';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useAuthStore } from '../../../store/authStore';
import { useAiTemplateFavoritesStore } from '../../../store/aiTemplateFavoritesStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useIncomeExpenseStore } from '../../../store/incomeExpenseStore';
import { moduleAutoFill } from '../../../lib/incomeExpenseDefaults';
import { optimizeImage } from '../../../utils/optimizeImage';
import { mergeImagesToGrid } from '../../../utils/mergeImagesToGrid';
import { isAiCapacityError } from '../../../utils/aiError';
import { todayISO, formatPromptDate, dateVariantValues } from '../../../utils/promptDate';
import CropStage from '../generate-image/CropStage';
import ColorSwatchPicker from '../../../components/common/ColorSwatchPicker';
import AiTemplateFeedbackModal from '../../../components/common/AiTemplateFeedbackModal';
import PhotoEditDrawer from '../../../components/common/PhotoEditDrawer';
import GeneratingOverlay from '../../../components/common/GeneratingOverlay';
import CompareSlider from '../../../components/common/CompareSlider';
import RecentPhotosStrip from '../../../components/common/RecentPhotosStrip';
import Carousel from '../../../components/common/EmblaCarousel';
import { makeThumbDataUrl } from '../../../utils/pendingGenJobs';
import { ActionBtn, EditToggle, InfoButton, PrintButton, btnIcon, customSizeIcon } from '../../../components/common/PhotoActionButtons';
import { sanitizeText } from '../../../utils/sanitize';
import { NEUTRAL_EDITS, bakePhotoEdits, isEdited, useFilterThumbs, usePhotoEditPreview } from '../../../utils/photoEdits';
import { copyImageToClipboard } from '../../../utils/imageShare';

// Audience badge config for the "this template is for X" chip on the header.
// Keys match AiTemplate.genders values set by the admin.
const GENDER_BADGES = {
    male:     { icon: IconGenderMale,   i18n: 'worldCupJersey.genderMale' },
    female:   { icon: IconGenderFemale, i18n: 'worldCupJersey.genderFemale' },
    children: { icon: IconMoodKid,      i18n: 'worldCupJersey.genderChildren' },
    product:  { icon: IconShoppingBag,  i18n: 'worldCupJersey.genderProduct' },
};

// ── Input class ─────────────────────────────────────────────────────────────
const fieldInputCls = "w-full px-3 py-2.5 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/40";

// Similar-styles strip: fixed card height; each card's width follows the
// template's REAL aspect ratio (from imageSize) so the thumbnail fills the
// card without top/bottom cropping.
const RENAME_MAX = 120; // a photo's name (Image info → rename)
const SIBLING_CARD_H = 150; // the similar-styles cards — small, so several templates fit in a row
const SIBLING_CARD_H_MOBILE = 130;
const SIBLING_RATIOS = { portrait_2x3: 2 / 3, portrait_4x5: 4 / 5, square_1x1: 1, landscape_3x2: 3 / 2, landscape_16x9: 16 / 9 };

// ── Aspect ratio configs ────────────────────────────────────────────────────

// Text input with an admin-defined suggestion dropdown: focusing shows the
// predefined values below the field; picking one fills it, typing stays
// free-form. onMouseDown (not onClick) so the pick lands before the blur.
function SuggestionInput({ value, onChange, suggestions, placeholder, maxLength, className }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <input
                type="text"
                className={className}
                placeholder={placeholder}
                value={value}
                maxLength={maxLength}
                autoComplete="off"
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onChange={(e) => onChange(e.target.value)}
            />
            {open && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-52 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg">
                    {suggestions.map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-orange-50 dark:hover:bg-zinc-700 ${
                                value === s ? 'text-primary font-semibold' : 'text-zinc-700 dark:text-zinc-200'
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AiTemplateGenerate() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { user } = useAuthStore();
    const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const compactActions = useMediaQuery('(max-width: 1279px)'); // the action buttons' icon-only range (names as tooltips)
    const { selectedOfficeSpace: rawSelectedOfficeSpace } = useOfficeSpaceStore();
    const isAdmin = user?.role === 'admin';
    const selectedOfficeSpace = isAdmin ? null : rawSelectedOfficeSpace;
    const userWantsWatermark = user?.settings?.applyAiTemplateWatermark !== false;
    const [hasPaidUser, setHasPaidUser] = useState(false);

    const GENERATING_STEPS = useMemo(() => [
        t('worldCupJersey.progress.reading'),
        t('worldCupJersey.progress.matching'),
        t('worldCupJersey.progress.applying'),
        t('worldCupJersey.progress.polishing'),
        t('worldCupJersey.progress.almostDone'),
    ], [t]);
    const STEP_DELAYS = [3000, 7000, 11000, 15000, 19000];

    // ── Core state ──────────────────────────────────────────────────────────
    const [template, setTemplate] = useState(null);
    const [loadingTpl, setLoadingTpl] = useState(true);
    // Per-user favorite (❤️ in the header) — same store as the gallery card hearts.
    const favSet = useAiTemplateFavoritesStore((s) => s.favorites);
    const toggleFavorite = useAiTemplateFavoritesStore((s) => s.toggle);
    const loadFavorites = useAiTemplateFavoritesStore((s) => s.load);
    useEffect(() => { loadFavorites(); }, [loadFavorites]);
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [extraFiles, setExtraFiles] = useState([]);
    const [extraPreviews, setExtraPreviews] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [generatingStep, setGeneratingStep] = useState(0);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [generatedImageId, setGeneratedImageId] = useState(null);
    // Background generations (the AI photo editor's): ✕ on a running generation keeps it going as a processing card in the recent
    // strip and frees the page for the next one; clicking a card brings it back. `jobsRef` keeps what a job needs to come back.
    // Original · Compare · Result on the result photo; a reopened result's original comes from its record (no upload on the page then).
    const [resultView, setResultView] = useState('result');
    const [reopenedOriginal, setReopenedOriginal] = useState(null);
    useEffect(() => { setResultView('result'); }, [generatedImageId]);
    const originalUrl = previewUrl || reopenedOriginal;
    const [bgJobs, setBgJobs] = useState([]);
    const genTokenRef = useRef(0);
    const foregroundTokenRef = useRef(null);
    const hiddenTokensRef = useRef(new Set());
    const jobsRef = useRef({}); // token → { file, extraFiles, previewUrl, extraPreviews, fieldValues, thumb }
    const [watermarkedUrl, setWatermarkedUrl] = useState(null);
    // ── Photo adjustments (ছবি এডিটিং) — same sliders as the generate-image tool.
    // Brightness/contrast/saturation live-preview via CSS filter; all four (incl.
    // sharpness) are baked into the file on download via canvas.
    // The result's edits — the Studio Editor's edit drawer (tools, filters, rotate / flip; utils/photoEdits.js). Never saved: the
    // download, print, copy and custom size bake them in. `editSource` = the result's bytes, fetched when the drawer first opens.
    const [edits, setEdits] = useState(NEUTRAL_EDITS);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editSource, setEditSource] = useState(null);
    const editSourceKeyRef = useRef(null);
    const adjustmentsDirty = isEdited(edits);
    const resetAdjustments = () => setEdits(NEUTRAL_EDITS);
    useEffect(() => { setEdits(NEUTRAL_EDITS); setEditorOpen(false); setEditSource(null); editSourceKeyRef.current = null; }, [generatedImageId]);
    const editedPreviewUrl = usePhotoEditPreview(editSource, edits);
    const filterThumbs = useFilterThumbs(editSource);
    // `?editId=t12` (the image viewer's "edit" on a template photo) opens that generated photo as this page's result — so the edit
    // drawer, download, print and copy work on it — instead of only showing the template. The param is dropped once read.
    const [searchParams, setSearchParams] = useSearchParams();
    const editLoadRef = useRef(null); // the editId being loaded (dropping the param re-runs this effect — it must not cancel the load)
    useEffect(() => {
        const editId = searchParams.get('editId');
        if (!editId || !template || editLoadRef.current === editId) return;
        editLoadRef.current = editId;
        setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('editId'); return next; }, { replace: true });
        imageGenerationService.getImageById(editId).then((res) => {
            const rec = res?.data;
            if (!rec?.aiImage) throw new Error('not found');
            setWatermarkedUrl(null);
            setReopenedOriginal(rec.uploadedImage || rec.originalImage || null);
            setGeneratedImage(rec.aiImage);
            setGeneratedImageId(rec.id || editId);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }).catch(() => {
            notifications.show({ title: t('imageGeneration.notifications.loadFailed'), message: t('imageGeneration.notifications.loadFailedMessage'), color: 'red' });
        }).finally(() => { editLoadRef.current = null; });
    }, [searchParams, template]); // eslint-disable-line react-hooks/exhaustive-deps
    // Like/dislike on the current result. feedbackState persists the chosen rating
    // ('positive' | 'negative' | null); feedbackLoading marks an in-flight submit.
    const [feedbackState, setFeedbackState] = useState(null);
    const [feedbackLoading, setFeedbackLoading] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [siblings, setSiblings] = useState([]);

    // ── Crop + rotate modal (for the primary uploaded image) ──────────────────
    const [cropSrc, setCropSrc] = useState(null);   // dataURL shown in the crop stage
    const [cropName, setCropName] = useState('');
    const pendingFileRef = useRef(null);            // original file (for skip/error fallback)
    const pendingTargetRef = useRef('primary');     // 'primary' | 'extra' — where the cropped file goes

    // ── History state ───────────────────────────────────────────────────────
    const [history, setHistory] = useState([]);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedHistoryImage, setSelectedHistoryImage] = useState(null);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printImageUrl, setPrintImageUrl] = useState('');
    const [printImageId, setPrintImageId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    // Custom size/quality download launched from the preview modal.
    const [customDlOpen, setCustomDlOpen] = useState(false);
    const [customDlUrl, setCustomDlUrl] = useState(null);
    const [customDlId, setCustomDlId] = useState(null);

    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const progressTimerRef = useRef(null);
    const stepTimersRef = useRef([]);

    // ── Derived ─────────────────────────────────────────────────────────────
    const templateFields = useMemo(() => {
        const raw = template?.fields;
        if (!raw) return [];
        try {
            const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
            return parsed.filter(f => f.key);
        } catch { return []; }
    }, [template]);

    const fieldsValid = useMemo(() => templateFields.every(f => {
        const val = (fieldValues[f.key] ?? '').toString().trim();
        if (f.required && !val) return false;
        if (!val) return true;
        if ((f.type === 'text' || f.type === 'textarea') && f.minLength && val.length < f.minLength) return false;
        if (f.type === 'number') {
            const num = Number(val);
            if (f.minValue != null && num < f.minValue) return false;
            if (f.maxValue != null && num > f.maxValue) return false;
        }
        return true;
    }), [templateFields, fieldValues]);

    const templateDisplayName = (i18n.language === 'bn' && template?.nameBn) ? template.nameBn : (template?.name || '');
    const requireUserImage = template ? String(template.requireUserImage) !== 'false' : true;
    // Admin-assigned audience tags (male/female/children/product) → header badges.
    const templateGenders = Array.isArray(template?.genders) ? template.genders.filter(g => GENDER_BADGES[g]) : [];

    // Collapse the audience tags into a single "who is this template for" notice
    // shown above the form, so the user uploads the right kind of photo.
    // male+female → both; a single tag → that tag; any other mix → both.
    const genderNotice = (() => {
        const s = new Set(templateGenders);
        if (s.size === 0) return null;
        if (s.has('male') && s.has('female')) return { key: 'both', icon: IconUsers };
        if (s.size === 1) {
            if (s.has('male')) return { key: 'male', icon: IconGenderMale };
            if (s.has('female')) return { key: 'female', icon: IconGenderFemale };
            if (s.has('children')) return { key: 'children', icon: IconMoodKid };
            if (s.has('product')) return { key: 'product', icon: IconShoppingBag };
        }
        return { key: 'both', icon: IconUsers };
    })();

    // A fresh result (new id) or a cleared preview (id → null) resets the rating.
    useEffect(() => {
        setFeedbackState(null);
        setFeedbackLoading(null);
        setShowFeedbackModal(false);
    }, [generatedImageId]);

    // ── Like/dislike handlers ─────────────────────────────────────────────────
    const submitFeedback = async (feedback, reason = null, note = null) => {
        if (!generatedImageId) return;
        setFeedbackLoading(feedback);
        try {
            await imageGenerationService.submitFeedback(generatedImageId, feedback, reason, note);
            setFeedbackState(feedback);
            setShowFeedbackModal(false);
            notifications.show({ message: t('worldCupJersey.feedback.thanks'), color: 'green', autoClose: 3000 });
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            notifications.show({ message: t('worldCupJersey.feedback.failed'), color: 'red' });
        } finally {
            setFeedbackLoading(null);
        }
    };
    // Feedback is one-shot: once a rating is recorded it can't be changed or re-edited.
    const handleLike = () => { if (!feedbackState && !feedbackLoading) submitFeedback('positive'); };
    const handleDislike = () => { if (!feedbackState && !feedbackLoading) setShowFeedbackModal(true); };
    const isMulti = !!template?.allowMultipleImages;
    // Image inputs = [{ label, required }] where `label` is a key from the labels file
    // (old templates stored plain strings/literals). Resolve the key → text in the
    // current UI language; a literal falls through unchanged.
    const imageInputs = useMemo(() => (Array.isArray(template?.imageLabels) ? template.imageLabels : []).map(v => {
        const raw = (typeof v === 'object' && v !== null) ? v : { label: v, required: true };
        return { label: resolveImageInputLabel(raw.label ?? '', i18n.language), required: raw.required !== false };
    }), [template?.imageLabels, i18n.language]);
    const MAX_TEMPLATE_IMAGES = 6;
    const definedSlotCount = isMulti ? Math.max(1, imageInputs.length) : 1;
    const maxImages = isMulti ? Math.max(MAX_TEMPLATE_IMAGES, definedSlotCount) : 1;
    const uploadedCount = (file ? 1 : 0) + extraFiles.length;
    // Minimum photos required — positional: last REQUIRED input index + 1 (at least
    // 1 when the template requires a photo). Optional trailing inputs don't count.
    const requiredMin = !requireUserImage ? 0 : (isMulti
        ? Math.max(1, imageInputs.reduce((m, inp, i) => inp.required ? Math.max(m, i + 1) : m, 0))
        : 1);
    const allFilesReady = uploadedCount >= requiredMin;
    // Slots to render: the defined ones, plus any extra uploaded beyond them.
    const renderSlotCount = isMulti ? Math.min(maxImages, Math.max(definedSlotCount, uploadedCount)) : 1;
    // Show the "+" tile only once every rendered slot is filled and we're under the cap.
    const showAddMoreTile = isMulti && uploadedCount === renderSlotCount && uploadedCount < maxImages;
    // Flattened upload list (primary first, then extras) for rendering the slot grid.
    const uploadedImages = useMemo(() => {
        const list = [];
        if (file) list.push({ url: previewUrl, primary: true });
        extraFiles.forEach((f, i) => list.push({ url: extraPreviews[i], primary: false }));
        return list;
    }, [file, previewUrl, extraFiles, extraPreviews]);
    // Preview always 16:9 — ratio selection only affects the generated output
    const previewRatio = '16 / 9';

    // Preselect a sensible default for every field so the user starts from a
    // filled-in state: the first choice for dropdown/options, and the default
    // colour (or first swatch) for a colorpicker. Runs whenever the template's
    // fields change (i.e. when a new template loads).
    // Build the "starting" field values for the current template: first choice for
    // dropdown/options, default (or first) swatch for colorpicker, blank for the rest.
    // Reused on template load AND when the close button resets the page to initial.
    const buildDefaultFieldValues = useCallback(() => {
        const defaults = {};
        templateFields.forEach(f => {
            if (f.type === 'colorpicker') {
                const v = f.defaultColor || f.choices?.[0]?.value || '';
                if (v) defaults[f.key] = v;
            } else if (f.type === 'dropdown' || f.type === 'options') {
                const c = (f.choices || [])[0];
                if (c) defaults[f.key] = c.value ?? c.label ?? '';
            } else if (f.type === 'date') {
                // Prefill today so auto mode needs no input and manual mode starts sensible.
                defaults[f.key] = todayISO();
            } else if (f.type === 'text' || f.type === 'textarea') {
                // Placeholder doubles as the starting value — the user keeps or edits it.
                const ph = (f.placeholder || '').trim();
                if (ph) defaults[f.key] = ph.slice(0, f.charLimit || 250);
            } else if (f.type === 'number') {
                // Only when the placeholder is an actual number, not a hint like "e.g. 5".
                const ph = (f.placeholder || '').trim();
                if (ph && !Number.isNaN(Number(ph))) defaults[f.key] = ph;
            }
        });
        return defaults;
    }, [templateFields]);

    useEffect(() => {
        if (!templateFields.length) { setFieldValues({}); return; }
        setFieldValues(buildDefaultFieldValues());
    }, [templateFields, slug]); // eslint-disable-line

    // Form-completion percentage shown in the left progress card (UI-only).
    const formProgress = useMemo(() => {
        let total = 0; let done = 0;
        if (requireUserImage) { total += 1; if (file) done += 1; }
        templateFields.forEach(f => {
            if (!f.required) return;
            total += 1;
            if ((fieldValues[f.key] ?? '').toString().trim()) done += 1;
        });
        return total === 0 ? 0 : Math.round((done / total) * 100);
    }, [requireUserImage, file, templateFields, fieldValues]);

    // "Similar styles" — other ACTIVE templates in the same, PUBLISHED category.
    useEffect(() => {
        if (!template?.categoryId) { setSiblings([]); return; }
        let cancelled = false;
        Promise.all([
            aiTemplateService.list({}),
            aiTemplateCategoryService.list(),
        ]).then(([r, catRes]) => {
            if (cancelled) return;
            const publishedCatIds = new Set(
                (catRes?.data || []).filter(c => c.status === 'published').map(c => c.id)
            );
            // Hide the whole section if the current template's category isn't published.
            if (!publishedCatIds.has(template.categoryId)) { setSiblings([]); return; }
            const all = r?.data || [];
            setSiblings(all.filter(tp =>
                tp.categoryId === template.categoryId &&
                tp.id !== template.id &&
                tp.status === 'active'
            ).slice(0, 10));
        }).catch(() => { if (!cancelled) setSiblings([]); });
        return () => { cancelled = true; };
    }, [template?.id, template?.categoryId]);

    // ── Load template ───────────────────────────────────────────────────────
    useEffect(() => {
        setFile(null); setPreviewUrl(null); setGeneratedImage(null); setGeneratedImageId(null);
        setWatermarkedUrl(prev => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return null; });
        let cancelled = false;
        setLoadingTpl(true);
        aiTemplateService.get(slug)
            .then(r => { if (!cancelled) setTemplate(r?.data || null); })
            .catch(() => { if (!cancelled) { notifications.show({ title: t('common.error'), message: t('worldCupJersey.templateNotFound'), color: 'red' }); navigate('/ai-templates'); } })
            .finally(() => { if (!cancelled) setLoadingTpl(false); });
        return () => { cancelled = true; };
    }, [slug, navigate]); // eslint-disable-line

    useEffect(() => () => {
        clearInterval(progressTimerRef.current);
        stepTimersRef.current.forEach(clearTimeout);
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    useEffect(() => {
        if (!isGenerating) { setGeneratingStep(0); return; }
        setGeneratingStep(0);
        stepTimersRef.current = STEP_DELAYS.map((d, i) => setTimeout(() => setGeneratingStep(i + 1), d));
        return () => stepTimersRef.current.forEach(clearTimeout);
    }, [isGenerating]); // eslint-disable-line

    // ── History loading ─────────────────────────────────────────────────────
    const requestedPageRef = useRef(1);
    // The guard is a ref: this callback only re-creates on an office-space change, so state read here would be stale.
    const historyLoadingRef = useRef(false);
    const loadHistory = useCallback(async (page = 1, append = false) => {
        if (historyLoadingRef.current && append) return;
        historyLoadingRef.current = true;
        setIsLoadingHistory(true);
        try {
            const r = await imageGenerationService.getHistory({ page, limit: 20, source: 'ai-templates', ...(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {}) });
            const incoming = r?.data?.history || [];
            const totalPages = r?.data?.pagination?.totalPages || r?.data?.totalPages || 1;
            setHistory(prev => append ? [...prev, ...incoming] : incoming);
            setHistoryPage(page);
            setHasMoreHistory(page < totalPages);
        } catch {
            if (append) requestedPageRef.current = page - 1; // let the strip ask again
        } finally { historyLoadingRef.current = false; setIsLoadingHistory(false); }
    }, [selectedOfficeSpace]); // eslint-disable-line

    useEffect(() => { setHistory([]); setHistoryPage(1); setHasMoreHistory(true); requestedPageRef.current = 1; loadHistory(1, false); }, [selectedOfficeSpace]); // eslint-disable-line
    // The recent strip's "next page" — fires on every scroll tick near the end, so ask for each page once.
    const loadMoreHistory = useCallback(() => {
        const next = historyPage + 1;
        if (historyLoadingRef.current || requestedPageRef.current >= next) return;
        requestedPageRef.current = next;
        loadHistory(next, true);
    }, [historyPage, loadHistory]);

    // ── Payment check ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!user || isAdmin) { setHasPaidUser(true); return; }
        (async () => {
            try {
                if (user.role === 'OPERATOR') {
                    if (!selectedOfficeSpace?.id) return;
                    const res = await paymentService.getOwnerHasPayment(selectedOfficeSpace.id);
                    setHasPaidUser(!!res?.hasPayment);
                } else {
                    const data = await paymentService.getHistory({ page: 1, limit: 20 });
                    setHasPaidUser((data?.transactions || []).some(tx => tx.status === 'SUCCESS'));
                }
            } catch {}
        })();
    }, [user?.id, user?.role, selectedOfficeSpace?.id, isAdmin]);

    // ── File handling ───────────────────────────────────────────────────────
    // Optimize the (already cropped) file and commit it as the primary image.
    const commitFile = useCallback(async (f) => {
        const optimized = await optimizeImage(f, { targetBytes: 500_000 }).catch(() => f);
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        setFile(optimized);
        setPreviewUrl(URL.createObjectURL(optimized));
        setExtraFiles([]); setExtraPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
        setGeneratedImage(null); setGeneratedImageId(null);
    }, [previewUrl]);

    // Optimize the (already cropped) file and append it as an extra image.
    const commitExtraFile = useCallback(async (f) => {
        const currentTotal = (file ? 1 : 0) + extraFiles.length;
        if (currentTotal >= maxImages) return;
        const optimized = await optimizeImage(f, { targetBytes: 500_000 }).catch(() => f);
        const newPreview = URL.createObjectURL(optimized);
        setExtraFiles(prev => [...prev, optimized]);
        setExtraPreviews(prev => [...prev, newPreview]);
    }, [file, extraFiles.length, maxImages]);

    // A newly selected image opens the crop + rotate stage first. `target` decides
    // whether the cropped result becomes the primary image or an extra one.
    const startCrop = useCallback((f, target = 'primary') => {
        if (!f || !f.type.startsWith('image/')) {
            if (f) notifications.show({ title: t('worldCupJersey.invalidFile'), message: t('worldCupJersey.invalidFileMessage'), color: 'red' });
            return;
        }
        if (f.size > 20 * 1024 * 1024) {
            notifications.show({ title: t('worldCupJersey.fileTooLarge'), message: t('worldCupJersey.fileTooLargeMessage'), color: 'red' });
            return;
        }
        pendingFileRef.current = f;
        pendingTargetRef.current = target;
        const reader = new FileReader();
        reader.onload = (e) => { setCropName(f.name || 'photo.jpg'); setCropSrc(e.target.result); };
        reader.readAsDataURL(f);
    }, [t]);

    const handleFileChange = useCallback((f) => startCrop(f, 'primary'), [startCrop]);

    // Upload-card tabs: 'upload' = the slot grid / dropzone, 'booth' = the photo-booth
    // queue. The booth tab exists only while the booth is ON for this office space.
    const [uploadTab, setUploadTab] = useState('upload');
    const [boothOn, setBoothOn] = useState(false);
    // Booth photos already taken into the slots — stay checked on the booth tab.
    // Cleared once every uploaded image is removed (slots can't be tied back to
    // individual booth ids after optimization, so marks reset wholesale).
    const [boothMarkedIds, setBoothMarkedIds] = useState([]);
    useEffect(() => { if (!file && extraFiles.length === 0) setBoothMarkedIds([]); }, [file, extraFiles.length]);

    // SINGLE-image templates: a booth tap replaces the primary via the normal crop flow.
    const handleBoothPick = useCallback(
        (f) => startCrop(f, (file && isMulti) ? 'extra' : 'primary'),
        [startCrop, file, isMulti],
    );

    // ── Booth crop queue ─────────────────────────────────────────────────────
    // After সিলেক্ট, the chosen photos are cropped ONE BY ONE through the normal
    // crop stage; each commit marks its booth photo, and when the queue drains the
    // view jumps to the আপলোড tab showing the cropped slots. Closing the crop stage
    // aborts whatever is left in the queue.
    const boothQueueRef = useRef([]);       // remaining [{ file, id, target }]
    const boothActiveIdRef = useRef(null);  // booth id of the photo now in the crop stage
    const startNextBoothCrop = useCallback(() => {
        const next = boothQueueRef.current.shift();
        if (!next) { setUploadTab('upload'); return; }
        boothActiveIdRef.current = next.id;
        startCrop(next.file, next.target);
    }, [startCrop]);
    // Called after a booth item commits (crop applied/skipped): mark it, move on.
    const finishBoothItem = useCallback(() => {
        const id = boothActiveIdRef.current;
        if (id == null) return;
        boothActiveIdRef.current = null;
        setBoothMarkedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        startNextBoothCrop();
    }, [startNextBoothCrop]);
    const abortBoothQueue = useCallback(() => {
        boothQueueRef.current = [];
        boothActiveIdRef.current = null;
    }, []);

    // Bulk-edit-style booth pick: taps select, সিলেক্ট starts the crop queue.
    // Single-image templates take only the first selection (replacing the primary);
    // multi-image templates fill primary first, then extras, capped to maxImages.
    const handleBoothPickFiles = useCallback((files, ids) => {
        if (!files.length) return;
        let pairs = files.map((f, i) => ({ file: f, id: ids[i] }));
        if (!isMulti) {
            pairs = pairs.slice(0, 1).map(p => ({ ...p, target: 'primary' }));
        } else {
            const capacity = maxImages - (file ? 1 : 0) - extraFiles.length;
            pairs = pairs.slice(0, Math.max(0, capacity))
                .map((p, i) => ({ ...p, target: (!file && i === 0) ? 'primary' : 'extra' }));
            if (!pairs.length) return;
        }
        boothQueueRef.current = pairs;
        startNextBoothCrop();
    }, [isMulti, file, extraFiles.length, maxImages, startNextBoothCrop]);

    // Route the cropped (or skipped-original) file to the primary or extra slot.
    const commitPending = useCallback((f) => {
        if (pendingTargetRef.current === 'extra') commitExtraFile(f);
        else commitFile(f);
    }, [commitFile, commitExtraFile]);

    // After each commit, advance the booth crop queue (no-op for normal uploads).
    const handleCropApply = useCallback((croppedFile) => { setCropSrc(null); commitPending(croppedFile); finishBoothItem(); }, [commitPending, finishBoothItem]);
    const handleCropSkip = useCallback(() => { setCropSrc(null); if (pendingFileRef.current) commitPending(pendingFileRef.current); finishBoothItem(); }, [commitPending, finishBoothItem]);
    const handleCropError = useCallback(() => { setCropSrc(null); if (pendingFileRef.current) commitPending(pendingFileRef.current); finishBoothItem(); }, [commitPending, finishBoothItem]);

    const handleAddMoreFiles = useCallback((fileList) => {
        const valid = Array.from(fileList).filter(f => f.type.startsWith('image/'));
        if (!valid.length) return;
        const currentTotal = (file ? 1 : 0) + extraFiles.length;
        if (currentTotal >= maxImages) return;
        startCrop(valid[0], 'extra');
    }, [file, extraFiles.length, maxImages, startCrop]);

    const handleRemoveExtraFile = useCallback((idx) => {
        setExtraPreviews(prev => { URL.revokeObjectURL(prev[idx]); return prev.filter((_, i) => i !== idx); });
        setExtraFiles(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const clearUpload = () => {
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        if (watermarkedUrl?.startsWith('blob:')) URL.revokeObjectURL(watermarkedUrl);
        setExtraPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
        setFile(null); setPreviewUrl(null); setExtraFiles([]); setGeneratedImage(null); setGeneratedImageId(null); setWatermarkedUrl(null);
        setReopenedOriginal(null);
    };

    // Close (×) on the result → return the page to its just-loaded state: drop the
    // uploaded photo + generated result AND reset every form field to its default,
    // so the left panel comes back exactly as it was on a fresh page load.
    const resetToInitial = () => {
        clearUpload();
        setFieldValues(buildDefaultFieldValues());
        resetAdjustments();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Remove the image at slot index `j`. Removing the primary (0) promotes the
    // first extra into its place so the remaining slots stay left-aligned.
    const removeSlot = useCallback((j) => {
        if (j === 0) {
            if (extraFiles.length) {
                if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
                setFile(extraFiles[0]);
                setPreviewUrl(extraPreviews[0]);
                setExtraFiles(prev => prev.slice(1));
                setExtraPreviews(prev => prev.slice(1));
                setGeneratedImage(null); setGeneratedImageId(null);
            } else {
                clearUpload();
            }
        } else {
            handleRemoveExtraFile(j - 1);
        }
    }, [extraFiles, extraPreviews, previewUrl, handleRemoveExtraFile]); // eslint-disable-line

    const openFilePicker = () => fileInputRef.current?.click();
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); const dropped = e.dataTransfer.files; if (dropped?.length) handleFileChange(dropped[0]); };

    // Paste-to-upload: grab an image from the clipboard (Cmd/Ctrl+V) and route it
    // into the primary slot, or as an extra file when the template needs more.
    useEffect(() => {
        if (!requireUserImage) return;
        const onPaste = (e) => {
            const active = document.activeElement;
            if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
            const items = e.clipboardData?.items;
            if (!items) return;
            const item = Array.from(items).find(i => i.type.startsWith('image/'));
            if (!item) return;
            const pasted = item.getAsFile();
            if (!pasted) return;
            e.preventDefault();
            if (!file) handleFileChange(pasted);
            else if (template?.allowMultipleImages && uploadedCount < maxImages) handleAddMoreFiles([pasted]);
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [requireUserImage, file, template?.allowMultipleImages, uploadedCount, maxImages, handleFileChange, handleAddMoreFiles]);

    // Ctrl/Cmd+U opens the file picker for the upload area.
    useEffect(() => {
        if (!requireUserImage) return;
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
                e.preventDefault();
                if (!file) openFilePicker();
                else if (template?.allowMultipleImages && uploadedCount < maxImages) addMoreInputRef.current?.click();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [requireUserImage, file, template?.allowMultipleImages, uploadedCount, maxImages]);

    // ── Watermark ───────────────────────────────────────────────────────────
    const applyWatermark = useCallback(async (imageId, wxPct, wyPct) => {
        try {
            const loadImg = (src) => new Promise((res, rej) => { const img = new Image(); img.onload = () => res(img); img.onerror = rej; img.src = src; });
            const imageBlob = await imageGenerationService.downloadImage(imageId, 'ai');
            const imageSrc = URL.createObjectURL(imageBlob);
            const [photo, logo] = await Promise.all([loadImg(imageSrc), loadImg('/images/logo-white.svg')]);
            URL.revokeObjectURL(imageSrc);
            const canvas = document.createElement('canvas');
            canvas.width = photo.naturalWidth; canvas.height = photo.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(photo, 0, 0);
            const logoW = Math.min(Math.round(canvas.width * 0.22), 160);
            const logoH = Math.round(logoW * (logo.naturalHeight / logo.naturalWidth));
            const xPct = wxPct != null ? Number(wxPct) : 80;
            const yPct = wyPct != null ? Number(wyPct) : 88;
            ctx.globalAlpha = 0.7;
            ctx.drawImage(logo, Math.round(canvas.width * (xPct / 100)) - Math.round(logoW / 2), Math.round(canvas.height * (yPct / 100)) - Math.round(logoH / 2), logoW, logoH);
            ctx.globalAlpha = 1;
            return await new Promise(res => canvas.toBlob(b => res(URL.createObjectURL(b)), 'image/jpeg', 0.93));
        } catch { return null; }
    }, []);

    // ── Generate ────────────────────────────────────────────────────────────
    const handleGenerate = useCallback(async () => {
        if (!template || !allFilesReady) return;
        if (!isAdmin && !selectedOfficeSpace) {
            notifications.show({ title: t('worldCupJersey.workspaceNeeded'), message: t('worldCupJersey.workspaceNeededMessage'), color: 'red' });
            return;
        }
        const token = ++genTokenRef.current;
        foregroundTokenRef.current = token;
        jobsRef.current[token] = { file, extraFiles, previewUrl, extraPreviews, fieldValues, thumb: previewUrl ? null : (template.thumbnail || null) };
        if (previewUrl) {
            makeThumbDataUrl(previewUrl).then((thumb) => {
                if (!thumb) return;
                if (jobsRef.current[token]) jobsRef.current[token].thumb = thumb;
                setBgJobs((prev) => prev.map((j) => (j.token === token ? { ...j, previewUrl: thumb } : j)));
            });
        }
        const isForeground = () => foregroundTokenRef.current === token && !hiddenTokensRef.current.has(token);
        setIsGenerating(true); setGeneratedImage(null); setProgress(0); setReopenedOriginal(null);
        try {
            const fd = new FormData();
            // Multi-image templates: merge every uploaded photo into ONE grid image so the
            // AI receives a single composite reference. If merging fails, fall back to
            // sending them separately as before.
            const allImages = [file, ...extraFiles].filter(Boolean);
            let mergedImage = null;
            if (isMulti && allImages.length > 1) {
                try { mergedImage = await mergeImagesToGrid(allImages); } catch { mergedImage = null; }
            }
            if (mergedImage) {
                fd.append('image', mergedImage);
            } else {
                if (file) fd.append('image', file);
                extraFiles.forEach((ef, i) => fd.append(`image_${i + 2}`, ef));
            }
            if (selectedOfficeSpace?.id) fd.append('officeSpaceId', selectedOfficeSpace.id);
            fd.append('displayName', `${templateDisplayName || template.name} - ${new Date().toISOString().slice(0, 10)}`);
            fd.append('aiTemplateId', String(template.id));
            fd.append('aiTemplateGeneration', 'true');
            Object.entries(fieldValues).forEach(([key, val]) => {
                // Date fields hold an ISO date — send a human-readable date in the
                // field's language instead; auto mode always uses today's date.
                const f = templateFields.find(tf => tf.key === key);
                const out = f?.type === 'date'
                    ? formatPromptDate(f.dateAuto !== false ? todayISO() : (val || todayISO()), f.dateLang)
                    : (val ?? '');
                fd.append(key, out);
            });
            // Auto-date variables — the backend substitutes any {date-*} the admin
            // placed in the prompt with today's date in that format; unused keys
            // are a no-op, so these are sent unconditionally.
            Object.entries(dateVariantValues()).forEach(([key, val]) => {
                if (!templateFields.some(tf => tf.key === key)) fd.append(key, val);
            });

            const response = await aiTemplateService.generate(template.id, fd);
            if (response?.data && !isForeground()) {
                // Finished while hidden: the photo lands in the recent strip, its processing card goes.
                hiddenTokensRef.current.delete(token);
                setBgJobs((prev) => prev.filter((j) => j.token !== token));
                setHistory([]); setHistoryPage(1); setHasMoreHistory(true); requestedPageRef.current = 1; loadHistory(1, false);
                notifications.show({ title: t('worldCupJersey.doneTitle'), message: t('imageGeneration.notifications.backgroundImageReady'), color: 'green' });
                return;
            }
            if (response?.data) {
                setProgress(100);
                setGeneratedImage(response.data.aiImage);
                setGeneratedImageId(response.data.id);
                setHistory([]); setHistoryPage(1); setHasMoreHistory(true); requestedPageRef.current = 1; loadHistory(1, false);
                notifications.show({ title: t('worldCupJersey.doneTitle'), message: t('worldCupJersey.doneMessage'), color: 'green' });
                const shouldWatermark = false; // SohozKaj logo/watermark disabled for all users (paid + non-paid)
                if (shouldWatermark) {
                    applyWatermark(response.data.id, template.watermarkX, template.watermarkY).then(async (url) => {
                        if (!url) return;
                        setWatermarkedUrl(prev => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return url; });
                        try { const blob = await fetch(url).then(r => r.blob()); const fd2 = new FormData(); fd2.append('image', blob, 'watermarked.jpg'); await imageGenerationService.replaceAiImage(response.data.id, fd2); } catch {}
                    });
                }
            } else throw new Error('No data');
        } catch (err) {
            if (!isForeground()) {
                hiddenTokensRef.current.delete(token);
                setBgJobs((prev) => prev.map((j) => (j.token === token ? { ...j, status: 'error' } : j)));
                notifications.show({ title: t('common.error'), message: t('imageGeneration.notifications.backgroundImageFailed'), color: 'red' });
                return;
            }
            let msg = err?.message || t('worldCupJersey.generationFailed');
            // AI model out of balance / rate-limited → show a friendly message, not the raw provider error.
            if (isAiCapacityError(err)) msg = t('imageGeneration.aiBusy');
            else if (!requireUserImage && /image.*required|required.*image/i.test(msg)) msg = t('worldCupJersey.generationFailed');
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            delete jobsRef.current[token];
            // Only the run in front owns the busy state: a hidden one finishing must not stop whatever is generating now.
            if (foregroundTokenRef.current === token) {
                setIsGenerating(false);
                foregroundTokenRef.current = null;
            }
        }
    }, [template, isMulti, file, extraFiles, previewUrl, extraPreviews, selectedOfficeSpace, isAdmin, t, loadHistory, fieldValues, templateFields, allFilesReady, hasPaidUser, userWantsWatermark, applyWatermark, requireUserImage, templateDisplayName]);

    // ✕ on a running generation: it keeps going, its card moves to the recent strip and the page is free for the next photo. The
    // job's photos are not revoked — it may be brought back.
    const hideCurrentGeneration = () => {
        const token = foregroundTokenRef.current;
        if (token == null || !isGenerating) return;
        hiddenTokensRef.current.add(token);
        const job = jobsRef.current[token];
        setBgJobs((prev) => [{ token, previewUrl: job?.thumb || null, status: 'processing' }, ...prev]);
        foregroundTokenRef.current = null;
        setIsGenerating(false);
        setFile(null); setPreviewUrl(null); setExtraFiles([]); setExtraPreviews([]);
        setGeneratedImage(null); setGeneratedImageId(null); setWatermarkedUrl(null);
    };
    // A processing card clicked → that generation comes back to the preview (the one in front, if any, swaps to the strip).
    const bringJobToForeground = (job) => {
        if (!job || job.status !== 'processing') return;
        const saved = jobsRef.current[job.token];
        if (!saved) return;
        if (foregroundTokenRef.current != null && isGenerating) hideCurrentGeneration();
        hiddenTokensRef.current.delete(job.token);
        foregroundTokenRef.current = job.token;
        setBgJobs((prev) => prev.filter((j) => j.token !== job.token));
        setFile(saved.file); setPreviewUrl(saved.previewUrl); setExtraFiles(saved.extraFiles || []); setExtraPreviews(saved.extraPreviews || []);
        setFieldValues(saved.fieldValues || {});
        setGeneratedImage(null); setGeneratedImageId(null); setWatermarkedUrl(null);
        setIsGenerating(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const dismissJob = (job) => setBgJobs((prev) => prev.filter((j) => j.token !== job.token));

    // Ctrl/Cmd+G starts generation when the form is ready.
    const canGenerate = fieldsValid && allFilesReady && !isGenerating && !generatedImage;
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
                e.preventDefault();
                if (canGenerate) handleGenerate();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canGenerate, handleGenerate]);

    // The result's bytes (the watermarked copy when there is one, else the stored image through the backend — the R2 host sends no
    // CORS headers), and the same with the drawer's edits burnt in.
    const getResultBlob = async (imageId = generatedImageId) => (
        imageId === generatedImageId && watermarkedUrl
            ? fetch(watermarkedUrl).then((r) => r.blob())
            : imageGenerationService.downloadImage(imageId, 'ai')
    );
    const getEditedResultBlob = async () => {
        const raw = await getResultBlob();
        return adjustmentsDirty ? bakePhotoEdits(raw, edits) : raw;
    };
    const loadEditSource = async () => {
        if (!generatedImageId || editSourceKeyRef.current === generatedImageId) return;
        editSourceKeyRef.current = generatedImageId;
        try { setEditSource(await getResultBlob()); } catch { editSourceKeyRef.current = null; }
    };
    const toggleEditor = () => setEditorOpen((o) => { if (!o) loadEditSource(); return !o; });
    // Bake the drawer's edits into a fresh blob URL (the download path).
    const applyAdjustmentsToUrl = async (srcUrl) => {
        const blob = await fetch(srcUrl).then((r) => r.blob());
        return URL.createObjectURL(await bakePhotoEdits(blob, edits));
    };
    // Print / custom size / copy carry the edits too.
    const openEditedPrint = async () => {
        const url = adjustmentsDirty ? URL.createObjectURL(await getEditedResultBlob()) : (watermarkedUrl || generatedImage);
        setPrintImageUrl(url); setPrintImageId(generatedImageId); setShowPrintModal(true);
    };
    const openEditedCustomDownload = async () => {
        const url = adjustmentsDirty ? URL.createObjectURL(await getEditedResultBlob()) : (watermarkedUrl || generatedImage);
        setCustomDlUrl(url); setCustomDlId(generatedImageId); setCustomDlOpen(true);
    };
    const copyEditedResult = async () => {
        try {
            await copyImageToClipboard(getEditedResultBlob());
            notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green' });
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.shareFailed'), message: t('imageGeneration.notifications.shareFailedMessage'), color: 'red' });
        }
    };

    // ── Download ────────────────────────────────────────────────────────────
    const handleDownload = async (imageId = generatedImageId) => {
        if (!imageId) return;
        setDownloadingId(imageId);
        const shouldWatermark = false; // SohozKaj logo/watermark disabled for all users (paid + non-paid)
        try {
            let downloadHref, shouldRevoke = false;
            if (imageId === generatedImageId && watermarkedUrl) { downloadHref = watermarkedUrl; }
            else if (shouldWatermark) { const wmUrl = await applyWatermark(imageId, template.watermarkX, template.watermarkY); downloadHref = wmUrl || URL.createObjectURL(await imageGenerationService.downloadImage(imageId, 'ai')); shouldRevoke = true; }
            else { downloadHref = URL.createObjectURL(await imageGenerationService.downloadImage(imageId, 'ai')); shouldRevoke = true; }
            // Slider edits apply to the CURRENT result only (history strip downloads untouched).
            if (adjustmentsDirty && imageId === generatedImageId) {
                try {
                    const editedUrl = await applyAdjustmentsToUrl(downloadHref);
                    if (editedUrl) {
                        if (shouldRevoke) URL.revokeObjectURL(downloadHref);
                        downloadHref = editedUrl;
                        shouldRevoke = true;
                    }
                } catch { /* fall back to the unedited image */ }
            }
            const a = document.createElement('a'); a.href = downloadHref; a.download = `${templateDisplayName || 'ai-template'}-${Date.now()}.jpg`; document.body.appendChild(a); a.click(); document.body.removeChild(a);
            if (shouldRevoke) URL.revokeObjectURL(downloadHref);
            imageGenerationService.trackDownload(imageId).catch(() => {});
            if (user?.settings?.autoAppearIncomeExpense === true) {
                const fill = moduleAutoFill(user?.settings, 'ai_template');
                openIncomeExpenseModal({ name: fill.name, type: 'income', amount: fill.price, category: 'ai_template', referenceId: imageId });
            }
        } catch { notifications.show({ title: t('common.error'), message: t('worldCupJersey.downloadFailed'), color: 'red' }); }
        finally { setDownloadingId(null); }
    };

    // ── Loading state ───────────────────────────────────────────────────────
    // Image info popover state (the popover itself is built below)
    const [imageInfo, setImageInfo] = useState(null); // { id, fileName, timestamp, dims }
    const [renameEditing, setRenameEditing] = useState(false);
    const [renameVal, setRenameVal] = useState('');

    // Below `nav` (1024px) the form collapses to one rail icon: a tap opens the whole form as a floating panel
    // beside it, so the preview keeps the width instead of the form dropping under it.
    const railMode = useMediaQuery('(max-width: 1023px)');
    const [railOpen, setRailOpen] = useState(false);
    const railPanelRef = useRef(null);
    const [railMaxH, setRailMaxH] = useState(null);
    const floatOpen = railMode && railOpen;
    useEffect(() => { if (!railMode) setRailOpen(false); }, [railMode]);
    useEffect(() => {
        if (!floatOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setRailOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [floatOpen]);
    // The panel ends 8px above the generate bar + bottom nav, wherever its top lands (re-measured on scroll / resize)
    useLayoutEffect(() => {
        if (!floatOpen) return undefined;
        const measure = () => {
            const el = railPanelRef.current;
            if (!el) return;
            setRailMaxH(Math.max(240, Math.floor(window.innerHeight - el.getBoundingClientRect().top - 52 - 70 - 8)));
        };
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
    }, [floatOpen]);

    if (loadingTpl) return (
        <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-zinc-300 border-t-primary rounded-full animate-spin" />
        </div>
    );
    if (!template) return null;

    const isFav = favSet.has(Number(template.id));
    const displayImage = generatedImage ? (watermarkedUrl || generatedImage) : null;
    const thumbRatio = SIBLING_RATIOS[template?.imageSize] || 2 / 3; // the template thumbnail's width / height
    // Once a result exists we hide the whole left control panel and centre the result
    // image; the on-image action buttons take over. Closing (×) clears it and the
    // panel returns.
    const hasResult = !!generatedImage && !isGenerating;

    // ═════════════════════════════════════════════════════════════════════════
    // Similar-styles slider. Rendered inside the right panel on desktop, but on mobile it
    // must sit at the very BOTTOM (after the form) — since the right panel is reordered
    // above the form there, we render this as its own order-3 flex child on mobile instead.
    // Image info — the result's name (double-click to rename, like the AI photo editor), template, pixel size and date. Fetched when
    // the popover opens, once per photo.
    // (its state is declared above the loading return)
    const loadImageInfo = async (open) => {
        if (!open || !generatedImageId || (imageInfo?.id === generatedImageId && imageInfo.fileName)) return; // retry a photo whose name didn't come back
        const id = generatedImageId;
        const measure = new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(`${img.naturalWidth} × ${img.naturalHeight} px`);
            img.onerror = () => resolve(null);
            img.src = generatedImage;
        });
        const [rec, dims] = await Promise.all([imageGenerationService.getImageById(id).then((r) => r?.data).catch(() => null), measure]);
        // the single-photo lookup keeps the name in metadata; the history row has it as fileName
        const fileName = rec?.fileName || rec?.metadata?.originalFilename || history.find((h) => h.id === id)?.fileName || '';
        setImageInfo({ id, fileName, timestamp: rec?.timestamp || null, dims });
    };
    const saveRename = async () => {
        const next = sanitizeText(renameVal, { maxLen: RENAME_MAX }).trim();
        setRenameEditing(false);
        if (!next || !imageInfo || next === imageInfo.fileName) return;
        try {
            await imageGenerationService.updateImageName(imageInfo.id, next);
            setImageInfo((prev) => (prev?.id === imageInfo.id ? { ...prev, fileName: next } : prev));
            setHistory((prev) => prev.map((h) => (h.id === imageInfo.id ? { ...h, fileName: next } : h)));
            notifications.show({ title: t('imageGeneration.renamed'), message: t('imageGeneration.imageNameUpdated'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('imageGeneration.renameFailed'), color: 'red' });
        }
    };
    const infoReady = imageInfo?.id === generatedImageId;
    const infoRow = (label, value) => (
        <div className="flex items-start justify-between gap-3">
            <span className="shrink-0 text-zinc-500 dark:text-zinc-400">{label}:</span>
            <span className="min-w-0 break-words text-right font-medium text-zinc-800 dark:text-zinc-100">{value || '—'}</span>
        </div>
    );
    const imageInfoBody = !infoReady ? (
        <div className="flex justify-center py-4"><IconLoader2 size={20} className="animate-spin text-zinc-400" /></div>
    ) : (
        <div className="flex flex-col gap-1.5 text-sm">
            {infoRow(t('imageGeneration.fileName'), renameEditing ? (
                <input
                    type="text"
                    value={renameVal}
                    maxLength={RENAME_MAX}
                    autoFocus
                    onChange={(e) => setRenameVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenameEditing(false); }}
                    onBlur={saveRename}
                    className="w-48 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                />
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            className="inline-flex cursor-pointer items-center gap-1 border-b border-dashed border-current"
                            onDoubleClick={() => { setRenameVal(imageInfo.fileName); setRenameEditing(true); }}
                        >
                            {imageInfo.fileName || '—'}
                            <IconEdit size={13} className="shrink-0 opacity-60" onClick={() => { setRenameVal(imageInfo.fileName); setRenameEditing(true); }} />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[100001]">{t('imageGeneration.doubleClickToRename')}</TooltipContent>
                </Tooltip>
            ))}
            {infoRow(t('imageGeneration.infoTemplate'), templateDisplayName)}
            {infoRow(t('imageGeneration.dimensions'), imageInfo.dims)}
            {infoRow(t('imageGeneration.infoCreated'), imageInfo.timestamp && new Date(imageInfo.timestamp).toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' }))}
        </div>
    );

    // The photo's actions — the AI photo editor's right column beside the photo (labelled from `xl`, an icon strip with tooltips
    // below), and the phone's icon row under the photo.
    const actionTip = isMobile ? 'top' : 'left';
    const adjustPanel = (
        <div className={isMobile ? 'mx-auto flex w-fit items-center gap-1 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-950' : 'shrink-0 self-start rounded-xl border border-zinc-200 bg-white p-1.5 dark:border-zinc-700 dark:bg-zinc-900 xl:w-[220px] xl:p-3'}>
            {!isMobile && (
                <span className="mb-2.5 hidden items-center gap-2 text-sm font-bold text-zinc-800 dark:text-zinc-100 xl:flex">
                    <span className="h-[15px] w-[3px] rounded-sm bg-primary" />
                    {t('imageGeneration.imageActionsTitle')}
                </span>
            )}
            <div className={isMobile ? 'contents' : 'flex flex-col items-stretch gap-1 max-xl:rounded-xl max-xl:bg-zinc-100 max-xl:p-1 dark:max-xl:bg-zinc-950 xl:gap-2'}>
                {/* the photo's details first, like the AI photo editor */}
                <InfoButton compact={compactActions} tipSide={actionTip} side={isMobile ? 'top' : 'left'} onOpenChange={loadImageInfo}>{imageInfoBody}</InfoButton>
                <EditToggle compact={compactActions} tipSide={actionTip} open={editorOpen} edited={adjustmentsDirty} onClick={toggleEditor} />
                <ActionBtn compact={compactActions} tipSide={actionTip} icon={customSizeIcon} label={t('imageGeneration.customSizeDownload')} onClick={openEditedCustomDownload} />
                <ActionBtn compact={compactActions} tipSide={actionTip} icon={btnIcon('/images/photo-tools/copy.svg')} label={t('imageGeneration.copyPhoto')} tooltip={t('imageGeneration.copyTooltip')} onClick={copyEditedResult} />
                <ActionBtn compact={compactActions} tipSide={actionTip} icon={<IconDownload size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />} label={t('imageGeneration.downloadPhoto')} onClick={() => handleDownload()} loading={downloadingId === generatedImageId} />
                <PrintButton compact={compactActions} tipSide={actionTip} label={t('imageGeneration.printThisImage')} onClick={openEditedPrint} />
            </div>
        </div>
    );

    const similarStylesSlider = siblings.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-[18px]">
            <div className="flex items-center gap-2 mb-3.5">
                <span className="w-[3px] h-[15px] bg-primary rounded-sm" />
                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('worldCupJersey.similarStyles')}</span>
            </div>
            {/* the recent strip's smooth free drag; Embla swallows the click that ends a drag */}
            <Carousel dragFree slideGap={10} align="start" loop={false}>
                {siblings.map(s => {
                    const sName = (i18n.language === 'bn' && s.nameBn) ? s.nameBn : s.name;
                    const cardH = isMobile ? SIBLING_CARD_H_MOBILE : SIBLING_CARD_H;
                    const cardW = Math.round(cardH * (SIBLING_RATIOS[s.imageSize] || 1));
                    return (
                        <Carousel.Slide key={s.id}>
                        <button type="button" onClick={() => navigate(`/ai-templates/${s.slug}`)}
                            style={{ width: cardW, height: cardH }}
                            className="group block rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700 relative">
                            {s.thumbnail
                                ? <img src={s.thumbnail} alt={sName} loading="lazy" className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                                : <div className="w-full h-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center"><IconSparkles size={22} className="text-zinc-400" /></div>}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <span className="absolute bottom-0 left-0 right-0 px-2 py-1.5 text-[11px] font-semibold !text-white truncate">{sName}</span>
                        </button>
                        </Carousel.Slide>
                    );
                })}
            </Carousel>
        </div>
    );

    const railLabel = t('worldCupJersey.detailsSection');
    const railReady = fieldsValid && allFilesReady && (!requireUserImage || !!(file || extraFiles.length));

    return (
        <div className={`font-hind py-2 sm:py-5 w-full max-w-full overflow-x-clip ${railMode && !generatedImage && !isGenerating ? 'pb-24' : ''}`}>
            {/* Rail mode: a tap outside the floating panel closes it */}
            {floatOpen && <div className="fixed inset-0 z-30 bg-black/20 animate-in fade-in-0 duration-200" onClick={() => setRailOpen(false)} aria-hidden="true" />}
            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleFileChange(e.target.files[0]); e.target.value = ''; }} />
            <input ref={addMoreInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.length) handleAddMoreFiles(e.target.files); e.target.value = ''; }} />

            {/* Breadcrumb — desktop only; on mobile the back button covers navigation. */}
            <nav className="hidden sm:flex items-center gap-1.5 mb-3 text-[12px] text-zinc-500 dark:text-zinc-400 flex-wrap">
                <Link to="/ai-templates" className="hover:text-primary transition-colors">{t('navigation.aiTemplates') || 'এআই টেমপ্লেট'}</Link>
                {template.categoryRef && (
                    <>
                        <span className="text-zinc-300 dark:text-zinc-600">/</span>
                        <Link
                            to={`/ai-templates?category=${encodeURIComponent(template.categoryRef.slug || '')}`}
                            className="truncate max-w-[160px] hover:text-primary transition-colors"
                        >
                            {(i18n.language === 'bn' && template.categoryRef.nameBn) ? template.categoryRef.nameBn : template.categoryRef.name}
                        </Link>
                    </>
                )}
                <span className="text-zinc-300 dark:text-zinc-600">/</span>
                <span className="text-zinc-700 dark:text-zinc-200 font-medium truncate max-w-[220px]">{templateDisplayName}</span>
            </nav>

            {/* Back + title. On mobile this is a compact single row (back · title · heart);
                the category / audience badges and subtitle are desktop-only to avoid the
                cramped, wrapping header on small screens. */}
            <div className="flex items-center gap-2.5 sm:gap-3.5 mb-3 sm:mb-5">
                <button onClick={() => navigate('/ai-templates')} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 flex items-center justify-center text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0">
                    <IconArrowLeft size={19} />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                        <span className="text-base sm:text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 truncate max-w-full">{templateDisplayName}</span>
                        {template.categoryRef && (
                            <span className="hidden sm:inline-flex text-[11px] font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                                {(i18n.language === 'bn' && template.categoryRef.nameBn) ? template.categoryRef.nameBn : template.categoryRef.name}
                            </span>
                        )}
                        {/* Audience badge(s) — "this template is for X" (desktop only) */}
                        {templateGenders.map((g) => {
                            const Icon = GENDER_BADGES[g].icon;
                            return (
                                <span key={g} className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1 rounded-full">
                                    <Icon size={13} className="text-primary" />
                                    {t(GENDER_BADGES[g].i18n)}
                                </span>
                            );
                        })}
                    </div>
                    <p className="hidden sm:block text-[13px] text-zinc-500 dark:text-zinc-400 mt-0.5 line-clamp-1">{t('worldCupJersey.generateSubtitle')}</p>
                </div>
                {/* ❤️ Favorite toggle — same store as the gallery card hearts */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            onClick={() => toggleFavorite(template.id)}
                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl border flex items-center justify-center flex-shrink-0 transition-colors ${
                                isFav
                                    ? 'border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 text-red-500'
                                    : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 hover:text-red-500 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {isFav ? <IconHeartFilled size={19} /> : <IconHeart size={19} />}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="z-[2000] font-hind">
                        {isFav ? t('worldCupJersey.removeFavorite') : t('worldCupJersey.addFavorite')}
                    </TooltipContent>
                </Tooltip>
            </div>

            {/* ── Two-panel layout — collapses to a single centred column once a
                result is ready (left control panel hidden). ── */}
            <div className={`flex flex-row ${railMode ? 'gap-3' : 'gap-5'} items-start w-full max-w-full`}>

                {/* ════════════════════════════════════════════════════════════ */}
                {/* LEFT PANEL — Controls. On desktop it smoothly collapses to width
                    0 once a result exists (which lets the right panel glide to
                    centre); on mobile it just unmounts. */}
                {/* ════════════════════════════════════════════════════════════ */}
                {!hasResult && (
                <div
                    aria-disabled={isGenerating}
                    className={cn(
                        railMode ? 'sticky top-3 shrink-0' : 'flex-shrink-0 w-[404px]',
                        floatOpen && 'z-40',
                        isGenerating && 'pointer-events-none opacity-60 select-none',
                    )}
                >
                    {/* Rail mode: one icon (name as a tooltip); a tap opens the whole form in the floating panel */}
                    {railMode && (
                        <nav className="flex w-12 flex-col gap-1 rounded-2xl border border-zinc-200 bg-zinc-100 p-1 dark:border-zinc-700 dark:bg-zinc-950" aria-label={templateDisplayName}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => setRailOpen((o) => !o)}
                                        aria-label={railLabel}
                                        aria-expanded={floatOpen}
                                        className={cn(
                                            'relative flex h-10 w-10 items-center justify-center rounded-xl transition-all',
                                            floatOpen ? 'bg-primary text-white shadow-md shadow-primary/30' : 'bg-white text-zinc-500 ring-1 ring-zinc-200 hover:text-zinc-800 hover:ring-zinc-300 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700 dark:hover:text-zinc-100',
                                        )}
                                    >
                                        <IconForms size={19} strokeWidth={1.8} />
                                        {railReady && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="z-[2000]">{railLabel}</TooltipContent>
                            </Tooltip>
                        </nav>
                    )}
                    {/* The form. Rail mode: a floating panel beside the rail with every card (hidden while closed). */}
                    <div
                        ref={railPanelRef}
                        style={floatOpen && railMaxH ? { maxHeight: railMaxH } : undefined}
                        className={cn(
                            'flex flex-col gap-3.5',
                            railMode
                                ? cn('absolute left-full top-0 ml-2 w-[min(380px,calc(100vw-5.5rem))] overflow-y-auto rounded-2xl bg-zinc-100 p-2 shadow-2xl [scrollbar-color:rgb(161_161_170)_transparent] [scrollbar-width:thin] dark:bg-zinc-950', floatOpen ? 'animate-in fade-in-0 slide-in-from-left-2 duration-200' : 'hidden')
                                : 'w-[404px]',
                        )}
                    >

                    {railMode && (
                        <button type="button" onClick={() => setRailOpen(false)} aria-label={t('common.close')} className="sticky top-2 z-30 -mb-[50px] mr-2 mt-2 flex h-7 w-7 shrink-0 items-center self-end justify-center rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-800 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700">
                            <IconX size={15} />
                        </button>
                    )}
                    {/* Workspace warning */}
                    {!isAdmin && !selectedOfficeSpace && (
                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700">
                            <IconAlertCircle size={14} className="text-yellow-600 flex-shrink-0" />
                            <p className="text-xs text-yellow-700 dark:text-yellow-400">{t('worldCupJersey.selectWorkspace')}</p>
                        </div>
                    )}

                    {/* Upload card */}
                    {requireUserImage && (
                        <div className={cn('bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-[18px]', railMode && 'pr-11')}>
                            <div className="flex items-center gap-2 mb-3.5">
                                <span className="w-[3px] h-[15px] bg-primary rounded-sm" />
                                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('worldCupJersey.uploadSection')}</span>
                                <span className="ml-auto text-[10.5px] font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                                    {isMulti
                                        ? `${requiredMin.toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US')}+ ${t('worldCupJersey.imagesChip')}`
                                        : `1 ${t('worldCupJersey.imagesChip')}`}
                                </span>
                            </div>
                            {/* আপলোড / ফটো বুথ tabs — booth tab only while the booth is ON */}
                            {boothOn && (
                                <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800 mb-3.5">
                                    {[
                                        { id: 'upload', label: t('photoCollectorPanel.tabUpload') },
                                        { id: 'booth', label: t('photoCollectorPanel.title') },
                                    ].map((tb) => (
                                        <button key={tb.id} type="button" onClick={() => setUploadTab(tb.id)}
                                            className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${uploadTab === tb.id ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                            {tb.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* Booth pane — kept mounted (hidden) so the on/off status resolves.
                                Multi-image templates get bulk-style multi-select (taps choose,
                                সিলেক্ট commits all and jumps to আপলোড); already-taken photos stay
                                checked here. Single-image templates keep tap-to-load + crop. */}
                            <div className={boothOn && uploadTab === 'booth' ? undefined : 'hidden'}>
                                <PhotoBoothInbox
                                    multiSelect={isMulti}
                                    markedIds={boothMarkedIds}
                                    onPickFiles={handleBoothPickFiles}
                                    onPickFile={handleBoothPick}
                                    onStatusChange={setBoothOn}
                                />
                            </div>
                            <div className={boothOn && uploadTab === 'booth' ? 'hidden' : undefined}>
                            {template.allowMultipleImages ? (
                                /* Multi-image: defined slots (label + required) + a "+" tile for extras */
                                <div className="grid grid-cols-2 gap-3">
                                    {Array.from({ length: renderSlotCount }).map((_, j) => {
                                        const item = uploadedImages[j];
                                        const input = imageInputs[j];
                                        const label = input?.label;
                                        const optional = input ? input.required === false : true;
                                        return (
                                            <div key={j} className="flex flex-col gap-1.5">
                                                {item ? (
                                                    <div className="relative w-full aspect-square">
                                                        <img src={item.url} alt={label || `Image ${j + 1}`} className="w-full h-full rounded-xl object-cover border-2 border-primary" />
                                                        <button onClick={() => removeSlot(j)} className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white shadow">
                                                            <IconX size={11} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => (file ? addMoreInputRef.current?.click() : openFilePicker())}
                                                        className="w-full aspect-square rounded-xl border-2 border-dashed border-orange-200 dark:border-zinc-700 hover:border-primary bg-orange-50/50 dark:bg-zinc-800/50 flex flex-col items-center justify-center gap-2 px-2 text-center transition-colors"
                                                    >
                                                        <div className="w-11 h-11 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                                            <IconUpload size={20} className="text-primary" />
                                                        </div>
                                                        <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{label || t('worldCupJersey.uploadTitle') || 'ছবি আপলোড করুন'}</p>
                                                        <p className="text-[11px] text-zinc-400">{optional ? (t('worldCupJersey.optional') || 'Optional') : `JPG / PNG • ${t('worldCupJersey.maxFileSize') || 'সর্বোচ্চ ১০ MB'}`}</p>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {/* "+" tile — add extra photos up to the cap */}
                                    {showAddMoreTile && (
                                        <button
                                            onClick={() => addMoreInputRef.current?.click()}
                                            className="w-full aspect-square rounded-xl border-2 border-dashed border-orange-200 dark:border-zinc-700 hover:border-primary bg-orange-50/50 dark:bg-zinc-800/50 flex flex-col items-center justify-center gap-1.5 text-center transition-colors"
                                        >
                                            <div className="w-11 h-11 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                                <IconPlus size={22} className="text-primary" />
                                            </div>
                                            <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">{t('worldCupJersey.addMore') || 'Add more'}</p>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                /* Single image: full-width dropzone */
                                <>
                                    {!file ? (
                                        <div
                                            onClick={openFilePicker}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            className={`flex flex-col items-center justify-center gap-2 py-8 px-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                                                isDragging
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-orange-200 dark:border-zinc-700 hover:border-primary bg-orange-50/50 dark:bg-zinc-800/50'
                                            }`}
                                        >
                                            <div className="w-11 h-11 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                                <IconUpload size={20} className="text-primary" />
                                            </div>
                                            <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{imageInputs[0]?.label || t('worldCupJersey.uploadTitle') || 'টেনে আনুন বা ক্লিক করুন'}</p>
                                            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
                                                <span>JPG / PNG &bull; {t('worldCupJersey.maxFileSize') || 'সর্বোচ্চ ১০ MB'}</span>
                                                <kbd className="px-1.5 py-0.5 rounded border border-zinc-300 dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-800 text-[10px] font-medium text-zinc-600 dark:text-zinc-300">Ctrl+U</kbd>
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="relative rounded-xl border-2 border-dashed border-orange-200 dark:border-zinc-700 bg-orange-50/50 dark:bg-zinc-800/50 p-3">
                                            <button onClick={clearUpload} className="absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-red-500 text-white shadow" title={t('worldCupJersey.chooseDifferentPhoto')}>
                                                <IconX size={12} />
                                            </button>
                                            <img src={previewUrl} alt="Uploaded" className="w-full max-h-64 rounded-lg object-contain" />
                                            {imageInputs[0]?.label && <p className="mt-2 text-center text-xs text-zinc-500 dark:text-zinc-400 truncate">{imageInputs[0].label}</p>}
                                        </div>
                                    )}
                                </>
                            )}
                            </div>
                            {/* Admin's optional note for this template (tips on the kind of photo to upload) — plain text */}
                            {template.userNote && !(boothOn && uploadTab === 'booth') && (
                                <p className="mt-[5px] text-xs leading-relaxed text-red-600 dark:text-red-400 whitespace-pre-line break-words">
                                    {template.userNote}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Details card */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-[18px] space-y-3.5">
                        <div className="flex items-center gap-2">
                            <span className="w-[3px] h-[15px] bg-primary rounded-sm" />
                            <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('worldCupJersey.detailsSection')}</span>
                        </div>

                    {/* Dynamic fields */}
                    {templateFields.length > 0 && (
                        <div className="space-y-3">
                            {templateFields.map(field => {
                                const val = fieldValues[field.key] ?? '';
                                const setVal = (v) => setFieldValues(prev => ({ ...prev, [field.key]: v }));
                                const charLimit = field.charLimit || 250;
                                // Field label in the user's language (falls back to English).
                                const fieldLabel = (i18n.language === 'bn' && field.labelBn) ? field.labelBn : field.label;

                                const labelEl = (
                                    <label className="block text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1.5">
                                        {fieldLabel}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                                    </label>
                                );

                                // Admin-defined quick-fill suggestions for text/textarea —
                                // tap to fill, or keep typing a custom value.
                                const suggestions = (field.suggestions || []).filter(s => (s || '').trim());
                                const suggestionChips = suggestions.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                        {suggestions.map((s, i) => (
                                            <button key={i} type="button" onClick={() => setVal(s.slice(0, charLimit))}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${val === s ? 'bg-primary border-primary text-white' : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-primary/50 hover:text-primary'}`}
                                            >{s}</button>
                                        ))}
                                    </div>
                                );

                                if (field.type === 'date') return (
                                    <div key={field.key}>
                                        {labelEl}
                                        {field.dateAuto !== false ? (
                                            <>
                                                <div className={`${fieldInputCls} flex items-center gap-2 cursor-default select-none`}>
                                                    <IconCalendar size={16} className="text-primary flex-shrink-0" />
                                                    <span>{formatPromptDate(todayISO(), field.dateLang)}</span>
                                                </div>
                                                <p className="text-xs text-zinc-400 mt-0.5">{t('worldCupJersey.dateAutoNote')}</p>
                                            </>
                                        ) : (
                                            /* Shared popover calendar instead of the browser-native
                                               date input, so the picker matches the rest of the app.
                                               State stays an ISO string; convert at the boundary. */
                                            <DatePickerField
                                                value={(() => {
                                                    const d = new Date(`${val || todayISO()}T00:00:00`);
                                                    return Number.isNaN(d.getTime()) ? null : d;
                                                })()}
                                                onChange={(d) => {
                                                    if (!d) return setVal('');
                                                    const p = (n) => String(n).padStart(2, '0');
                                                    setVal(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
                                                }}
                                                triggerClassName={fieldInputCls}
                                                fromYear={2000}
                                                toYear={new Date().getFullYear() + 5}
                                            />
                                        )}
                                    </div>
                                );
                                if (field.type === 'textarea') return (
                                    <div key={field.key}>
                                        {labelEl}
                                        <textarea className={`${fieldInputCls} resize-none`} placeholder={field.placeholder || ''} value={val} onChange={e => setVal(e.target.value.replace(/[<>]/g, '').slice(0, charLimit))} maxLength={charLimit} rows={3} />
                                        {field.charLimit && <p className="text-xs text-zinc-400 mt-0.5 text-right">{val.length}/{field.charLimit}</p>}
                                        {suggestionChips}
                                    </div>
                                );
                                if (field.type === 'number') return (
                                    <div key={field.key}>
                                        {labelEl}
                                        <input type="number" className={fieldInputCls} placeholder={field.placeholder || ''} value={val} onChange={e => setVal(e.target.value)} min={field.minValue ?? undefined} max={field.maxValue ?? undefined} />
                                    </div>
                                );
                                if (field.type === 'dropdown') return (
                                    <div key={field.key}>
                                        {labelEl}
                                        <SelectField
                                            value={val || ''}
                                            onChange={v => setVal(v || '')}
                                            options={(field.choices || []).map(c => ({
                                                ...c,
                                                label: (i18n.language === 'bn' && c.labelBn) ? c.labelBn : c.label,
                                            }))}
                                            placeholder={field.placeholder || 'Select'}
                                            triggerClassName={fieldInputCls}
                                        />
                                    </div>
                                );
                                if (field.type === 'colorpicker') return (
                                    <div key={field.key}>
                                        {labelEl}
                                        <ColorSwatchPicker
                                            value={val}
                                            defaultColor={field.defaultColor}
                                            swatches={(field.choices || []).map(c => c.value).filter(Boolean)}
                                            onChange={setVal}
                                        />
                                    </div>
                                );
                                if (field.type === 'options') return (
                                    <div key={field.key}>
                                        <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-2">{fieldLabel}{field.required && <span className="text-red-500 ml-0.5">*</span>}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(field.choices || []).map(c => (
                                                <button key={c.value} type="button" onClick={() => setVal(c.value)}
                                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${val === c.value ? 'bg-primary border-primary text-white' : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-primary/50'}`}
                                                >{(i18n.language === 'bn' && c.labelBn) ? c.labelBn : c.label}</button>
                                            ))}
                                        </div>
                                    </div>
                                );
                                // default: text — with a suggestion dropdown when the admin
                                // predefined values for this field (pick one or type freely).
                                return (
                                    <div key={field.key}>
                                        {labelEl}
                                        {suggestions.length > 0 ? (
                                            <SuggestionInput
                                                className={fieldInputCls}
                                                placeholder={field.placeholder || ''}
                                                value={val}
                                                maxLength={charLimit}
                                                suggestions={suggestions}
                                                onChange={(v) => setVal(v.replace(/[<>]/g, '').slice(0, charLimit))}
                                            />
                                        ) : (
                                            <input type="text" className={fieldInputCls} placeholder={field.placeholder || ''} value={val} onChange={e => setVal(e.target.value.replace(/[<>]/g, '').slice(0, charLimit))} maxLength={charLimit} autoComplete="off" />
                                        )}
                                        {field.charLimit && <p className="text-xs text-zinc-400 mt-0.5 text-right">{val.length}/{field.charLimit}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Validation warning */}
                    {!fieldsValid && templateFields.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700">
                            <IconAlertCircle size={14} className="text-yellow-600 flex-shrink-0" />
                            <p className="text-xs text-yellow-700 dark:text-yellow-400">{t('worldCupJersey.fillRequiredFields')}</p>
                        </div>
                    )}

                    {/* Generate button — inline on desktop. On mobile it is rendered instead
                        as a fixed bottom bar (below), so it's always visible without scrolling. */}
                    {!generatedImage && !isGenerating && !railMode && (
                        <div className="sticky bottom-[52px] nav:bottom-0 z-20 pt-8 pb-4 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-zinc-900 dark:via-zinc-900/95">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleGenerate}
                                    disabled={!fieldsValid || !allFilesReady || isGenerating}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-gradient-to-r from-primary to-orange-600 hover:from-primary/90 hover:to-orange-600/90 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/25"
                                >
                                    <IconSparkles size={18} />
                                    {t('worldCupJersey.generate') || '+ তৈরি করুন'}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="z-[2000]">{`${t('worldCupJersey.generate') || '+ তৈরি করুন'} (Ctrl+G)`}</TooltipContent>
                        </Tooltip>
                        </div>
                    )}

                    {/* The result actions (Preview / Download) now live ON the result
                        image in the right panel, and this whole left panel is hidden
                        once a result exists — so no result buttons are rendered here. */}

                        {/* Helper */}
                        {/* <p className="text-center text-[11px] text-zinc-400 dark:text-zinc-500">{t('worldCupJersey.requiredHint')}</p> */}
                    </div>{/* end details card */}

                    {/* Form progress + audience notice — one combined card */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2.5">
                            <span className="text-[12.5px] font-semibold text-zinc-700 dark:text-zinc-200">{t('worldCupJersey.formProgress')}</span>
                            <span className="text-[12.5px] font-bold text-primary">{formProgress.toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US')}%</span>
                        </div>
                        <div className="w-full h-[7px] bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${formProgress}%` }} />
                        </div>

                        {/* Audience notice — who this template is designed for, so the
                            user uploads the matching kind of photo. */}
                        {genderNotice && (
                            <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center gap-2">
                                <genderNotice.icon size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                <p className="text-xs text-blue-700 dark:text-blue-300">{t(`worldCupJersey.genderNotice.${genderNotice.key}`)}</p>
                            </div>
                        )}
                        {/* Face-match disclaimer — the AI can miss the exact face; set
                            expectations up front so users don't blame their photo (or us). */}
                        {requireUserImage && genderNotice?.key !== 'product' && (
                            <div className={`${genderNotice ? 'mt-2' : 'mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800'} flex items-start gap-2`}>
                                <IconAlertCircle size={16} className="text-zinc-400 dark:text-zinc-500 flex-shrink-0 mt-px" />
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('worldCupJersey.aiFaceNote')}</p>
                            </div>
                        )}
                    </div>

                    {/* Template tags — the tags this template belongs to */}
                    {Array.isArray(template.tags) && template.tags.length > 0 && (
                        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-[18px]">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="w-[3px] h-[15px] bg-primary rounded-sm" />
                                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('navigation.tags') || 'ট্যাগ'}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {template.tags.map((tag) => (
                                    <button
                                        key={tag}
                                        onClick={() => navigate(`/ai-templates?tag=${encodeURIComponent(tag)}`)}
                                        className="inline-flex items-center text-[12px] font-medium text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1 rounded-full hover:bg-primary hover:text-white dark:hover:bg-primary transition-colors"
                                    >
                                        #{tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    </div>{/* end left-panel fixed-width inner */}
                </div>
                )}

                {/* ════════════════════════════════════════════════════════════ */}
                {/* RIGHT PANEL — Preview / Result                              */}
                {/* ════════════════════════════════════════════════════════════ */}
                <div className="min-w-0 w-full flex flex-col gap-4 flex-1">

                    {/* The dark stage, with the photo's actions to its right once there is a result */}
                    <div className={`flex min-w-0 ${isMobile ? 'flex-col' : 'flex-row items-start gap-3'}`}>
                    <div className={`group relative z-10 rounded-2xl sk-editor-stage overflow-hidden flex-1 w-full min-w-0 min-h-[420px] md:min-h-[max(560px,calc(100dvh-15rem))] flex items-center justify-center p-4 lg:p-8`}>

                        {/* The result — the AI photo editor's layout: Original · Compare · Result at the top-left (on hover), preview + ✕
                            at the top-right (always), like / dislike pills at the bottom (on hover), the edit drawer on the bottom edge */}
                        {displayImage && !isGenerating && (
                            <div className="relative mx-auto w-fit min-w-0 max-w-full overflow-hidden rounded-xl">
                                    {/* the result always sits here (it sizes the box — switching views never changes it); never taller than the
                                        screen, so the drawer on its bottom edge stays in view */}
                                    <img
                                        src={editedPreviewUrl || displayImage}
                                        alt="Generated"
                                        className="block h-auto max-h-[calc(100dvh-19rem)] w-auto max-w-full rounded-xl shadow-2xl"
                                        style={{ visibility: resultView === 'result' ? 'visible' : 'hidden' }}
                                    />
                                    {resultView === 'original' && originalUrl && (
                                        <img src={originalUrl} alt="" className="sk-editor-stage absolute inset-0 h-full w-full object-contain" />
                                    )}
                                    {resultView === 'compare' && originalUrl && (
                                        <div className="sk-editor-stage absolute inset-0">
                                            <CompareSlider fit="contain" before={originalUrl} after={editedPreviewUrl || displayImage} beforeLabel={t('studioEditor.before')} afterLabel={t('studioEditor.after')} />
                                        </div>
                                    )}
                                    {/* The edit drawer (the Studio Editor's) on the photo's bottom edge — on the result view */}
                                    <div style={resultView === 'result' ? undefined : { display: 'none' }}>
                                        <PhotoEditDrawer value={edits} onChange={setEdits} open={editorOpen} onClose={() => setEditorOpen(false)} thumbs={filterThumbs} />
                                    </div>

                                    {/* Original · Compare · Result */}
                                    {originalUrl && (
                                        <div
                                            role="group"
                                            className={`absolute left-2 top-2 z-[25] inline-flex rounded-lg bg-white/90 p-0.5 shadow-md backdrop-blur transition-[transform,opacity] duration-300 ease-out dark:bg-zinc-900/90 ${isMobile ? '' : 'pointer-events-none -translate-y-2 opacity-0 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100'}`}
                                        >
                                            {['original', 'compare', 'result'].map((v) => (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    aria-pressed={resultView === v}
                                                    onClick={() => setResultView(v)}
                                                    className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${resultView === v ? 'bg-primary text-white shadow-sm' : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300'}`}
                                                >
                                                    {t(`studioEditor.${v}`)}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Preview + close — always visible, at the photo's top-right corner */}
                                    <div className="absolute right-2 top-2 z-[25] flex items-center gap-1.5">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={() => { setSelectedHistoryImage({ id: generatedImageId, aiImage: watermarkedUrl || generatedImage }); setShowImageModal(true); }}
                                                    aria-label={t('worldCupJersey.preview')}
                                                    className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary text-white shadow-[0_4px_12px_rgba(0,0,0,0.5)] transition-transform hover:scale-105"
                                                >
                                                    <IconEye size={18} />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{t('worldCupJersey.preview')}</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={resetToInitial}
                                                    aria-label={t('common.close') || 'Close'}
                                                    className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-red-600 text-white shadow-[0_4px_12px_rgba(0,0,0,0.5)] transition-transform hover:scale-105"
                                                >
                                                    <IconX size={18} />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{t('common.close') || 'Close'}</TooltipContent>
                                        </Tooltip>
                                    </div>

                                    {/* Like / dislike — the AI photo editor's pills, just above the photo's bottom edge; on hover (always on a
                                        phone), only on the result view and not while the edit drawer is open; gone once rated */}
                                    {!feedbackState && resultView === 'result' && !editorOpen && (
                                        <div className={`absolute inset-x-0 bottom-3 z-[22] flex items-center justify-center gap-2 transition-opacity duration-200 ${isMobile ? '' : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'}`}>
                                            <button
                                                type="button"
                                                onClick={handleLike}
                                                disabled={!!feedbackLoading}
                                                className="inline-flex items-center gap-1.5 rounded-[10px] bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {feedbackLoading === 'positive' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbUp size={14} />}
                                                {t('worldCupJersey.feedback.likeLabel')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDislike}
                                                disabled={!!feedbackLoading}
                                                className="inline-flex items-center gap-1.5 rounded-[10px] bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {feedbackLoading === 'negative' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbDown size={14} />}
                                                {t('worldCupJersey.feedback.dislikeLabel')}
                                            </button>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Template thumbnail as placeholder */}
                        {!displayImage && !isGenerating && template.thumbnail && (
                            // sized from the template's shape before it loads (so the stage doesn't jump): as wide as the stage allows, never
                            // taller than the result may be — width-led, so a narrow stage scales it down instead of letterboxing it
                            <img
                                src={template.thumbnail}
                                alt={templateDisplayName}
                                className="block h-auto rounded-xl object-cover shadow-2xl"
                                style={{ aspectRatio: thumbRatio, width: `min(100%, calc((100dvh - 19rem) * ${thumbRatio}))` }}
                            />
                        )}

                        {/* Empty state — no thumbnail */}
                        {!displayImage && !isGenerating && !template.thumbnail && (
                            <div className="flex flex-col items-center gap-4 text-center px-6">
                                <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center">
                                    <IconPhoto size={36} className="text-white/60" />
                                </div>
                                <p className="text-white text-base font-semibold">{templateDisplayName}</p>
                                <p className="text-white/60 text-[12.5px] max-w-[320px] leading-relaxed">{t('worldCupJersey.previewHint')}</p>
                            </div>
                        )}

                        {/* Generating — the photo editors' cloud effect (no percentage); ✕ keeps it running in the background */}
                        {isGenerating && (
                            <GeneratingOverlay
                                photoUrl={previewUrl || template.thumbnail}
                                steps={GENERATING_STEPS}
                                stepDelays={STEP_DELAYS}
                                onHide={hideCurrentGeneration}
                            />
                        )}

                    </div>

                    {/* The photo's actions — beside the photo (desktop / tablet) */}
                    {!isMobile && hasResult && displayImage && adjustPanel}

                    </div>{/* end stage + actions row */}

                    {/* ছবি এডিটিং — mobile: below the result image. */}
                    {isMobile && hasResult && displayImage && adjustPanel}

                    {/* Similar styles — under the preview */}
                    {similarStylesSlider}

                </div>

            </div>

            {/* Recent generations — full page width; running background generations first */}
            {(history.length > 0 || bgJobs.length > 0) && (
                <RecentPhotosStrip
                    className="mt-5"
                    title={t('imageGeneration.recentGenerations')}
                    history={history}
                    onOpenHistory={(img) => { setSelectedHistoryImage(img); setShowImageModal(true); }}
                    bgJobs={bgJobs}
                    onOpenJob={bringJobToForeground}
                    onDismissJob={dismissJob}
                    viewAllTo="/documents?tab=images&sub=aiTemplate"
                    tileH={isMobile ? 136 : 160}
                    hasMore={hasMoreHistory}
                    loadingMore={isLoadingHistory}
                    onLoadMore={loadMoreHistory}
                />
            )}

            {/* Keyframes */}
            <style>{`
                @keyframes wcj-shimmer-sweep { 0% { transform: translateX(-120%) skewX(-12deg); } 100% { transform: translateX(280%) skewX(-12deg); } }
                @keyframes wcj-glow-pulse { 0%, 100% { opacity: 0.28; } 50% { opacity: 0.52; } }
                @keyframes atg-dot-bounce { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1.0); opacity: 1; } }
                .atg-blob { position: absolute; border-radius: 50%; filter: blur(26px); opacity: 1; mix-blend-mode: screen; animation-name: atg-blob-float; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
                @keyframes atg-blob-float {
                    0%   { transform: translate(0, 0) scale(1); }
                    25%  { transform: translate(22%, 16%) scale(1.25); }
                    50%  { transform: translate(-16%, 24%) scale(0.88); }
                    75%  { transform: translate(16%, -20%) scale(1.2); }
                    100% { transform: translate(0, 0) scale(1); }
                }
                .atg-sheen { animation: atg-sheen-drift 8s ease-in-out infinite; }
                @keyframes atg-sheen-drift { 0%, 100% { opacity: 0.6; transform: translateY(-2%); } 50% { opacity: 1; transform: translateY(2%); } }
                @keyframes atg-ring-glow { 0%, 100% { opacity: 0.35; transform: scale(0.94); } 50% { opacity: 0.7; transform: scale(1.06); } }
                .atg-ring-glow { animation: atg-ring-glow 2.4s ease-in-out infinite; }
                @keyframes atg-bar-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                .atg-bar-shimmer { background-size: 200% 100%; animation: atg-bar-shimmer 1.6s linear infinite; }
                @media (prefers-reduced-motion: reduce) { .atg-blob, .atg-sheen, .atg-ring-glow, .atg-bar-shimmer { animation: none; } }
            `}</style>

            {/* Image preview modal */}
            {showImageModal && (
                <ImagePreviewModal
                    opened={showImageModal}
                    onClose={() => setShowImageModal(false)}
                    images={history.some(h => h.id === selectedHistoryImage?.id) ? history : [selectedHistoryImage, ...history].filter(Boolean)}
                    initialImage={selectedHistoryImage}
                    hasMore={hasMoreHistory}
                    isLoadingMore={isLoadingHistory}
                    onLoadMore={() => loadHistory(historyPage + 1, true)}
                    onImageDeleted={(deletedId) => { setHistory(prev => prev.filter(img => img.id !== deletedId)); setShowImageModal(false); }}
                    onPrint={(imageUrl, imageId) => {
                        setPrintImageUrl(imageUrl);
                        setPrintImageId(imageId);
                        setShowPrintModal(true);
                    }}
                    onCustomDownload={(imageUrl, imageId) => {
                        setCustomDlUrl(imageUrl);
                        setCustomDlId(imageId);
                        setCustomDlOpen(true);
                    }}
                />
            )}

            <PrintModal
                opened={showPrintModal}
                onClose={() => {
                    // Drop the loaded image with the modal — the print sheet must
                    // start empty on the next open, not with this session's image.
                    setShowPrintModal(false);
                    setPrintImageUrl('');
                    setPrintImageId(null);
                }}
                imageUrl={printImageUrl}
                imageId={printImageId}
                initialSizeType="free"
                historySource="ai-templates"
            />

            <CustomDownloadModal
                opened={customDlOpen}
                onClose={() => setCustomDlOpen(false)}
                imageUrl={customDlUrl}
                imageId={customDlId}
            />

            {/* Crop + rotate stage for the uploaded image */}
            {cropSrc && (
                <div className="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-zinc-900" style={{ height: '100dvh' }}>
                    <CropStage
                        imageSrc={cropSrc}
                        fileName={cropName}
                        isMobile={isMobile}
                        t={t}
                        aspect={null}
                        hint={genderNotice?.key === 'product' ? '' : t('worldCupJersey.faceCropHint')}
                        focusFace={genderNotice?.key !== 'product'}
                        onClose={() => { abortBoothQueue(); setCropSrc(null); }}
                        onApply={handleCropApply}
                        onSkip={handleCropSkip}
                        onError={handleCropError}
                    />
                </div>
            )}

            {/* Dislike feedback — collects a reason + optional note */}
            <AiTemplateFeedbackModal
                open={showFeedbackModal}
                onOpenChange={setShowFeedbackModal}
                submitting={feedbackLoading === 'negative'}
                onSubmit={(reason, note) => submitFeedback('negative', reason, note)}
            />

            {/* Mobile generate bar — docked flush on the bottom nav (52px tall, see MobileBottomNav), on a solid strip so nothing
                scrolls through a gap under it or shows through the button while it's disabled. */}
            {railMode && !generatedImage && !isGenerating && (
                <div className="fixed left-0 right-0 bottom-[52px] z-40 border-t border-zinc-200 bg-white/95 px-3 pb-2 pt-2 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95">
                    <button
                        onClick={handleGenerate}
                        disabled={!fieldsValid || !allFilesReady || isGenerating}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-gradient-to-r from-primary to-orange-600 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_24px_-6px_rgba(244,123,22,0.6)]"
                    >
                        <IconSparkles size={18} />
                        {t('worldCupJersey.generate') || '+ তৈরি করুন'}
                    </button>
                </div>
            )}
        </div>
    );
}
